<i18n src="@/locales/components/RequestNotificationStepsConfigTranslate.json"></i18n>

<template>
	<div class="approver-dept-restrictions">
		<div v-if="!stepId" class="text-gray-2 text-center py-5">
			{{ $t("DeptRestrictionSaveStepFirst") }}
		</div>

		<div v-else>
			<div class="mb-4 text-sm text-gray-2">
				{{ $t("DeptRestrictionDescription") }}
			</div>

			<!-- Existing restrictions grouped by employee -->
			<div v-if="groupedRestrictions.length > 0" class="mb-5">
				<div
					v-for="group in groupedRestrictions"
					:key="group.employee_id"
					class="restriction-group border border-gray-5 rounded p-4 mb-3"
				>
					<div class="flex justify-between items-center mb-2">
						<h6 class="font-bold text-black">{{ group.employee_name }}</h6>
						<span
							class="text-danger text-sm cursor-pointer hover:underline"
							@click="removeEmployeeRestrictions(group.employee_id)"
						>{{ $t("Actions.Delete") }}</span>
					</div>
					<div class="flex flex-wrap gap-2">
						<span
							v-for="dept in group.departments"
							:key="dept.department_id"
							class="inline-flex items-center bg-gray-6 text-black rounded px-2 py-1 text-sm"
						>
							{{ dept.department_name }}
							<i
								class="nucleo nucleo-cancel-01 ml-1 cursor-pointer text-danger text-xs"
								@click="removeDepartment(group.employee_id, dept.department_id, group)"
							></i>
						</span>
					</div>
				</div>
			</div>

			<div v-else-if="!isLoading" class="text-gray-2 text-center py-3 mb-4">
				{{ $t("DeptRestrictionNoRestrictions") }}
			</div>

			<!-- Add new restriction -->
			<div class="add-restriction border border-dashed border-gray-4 rounded p-4">
				<h6 class="font-bold mb-3">{{ $t("DeptRestrictionAddNew") }}</h6>

				<div class="mb-3">
					<cbs-relations-humanroles
						class="select-multiple w-full"
						v-model="newEmployee"
						:type="0"
						:multiple="false"
						filter="Employee"
						:title="$t('DeptRestrictionSelectEmployee')"
						:placeholder="$t('DeptRestrictionEmployeePlaceholder')"
					/>
				</div>

				<div class="mb-3" v-if="newEmployee">
					<cbs-departments
						v-model="newDepartments"
						multiple
						:title="$t('DeptRestrictionSelectDepartments')"
					/>
				</div>

				<vs-button
					v-if="newEmployee && newDepartments && newDepartments.length > 0"
					size="small"
					type="border"
					@click="saveRestriction"
					:disabled="isSaving"
				>{{ $t("Actions.Save") }}</vs-button>
			</div>

			<div v-if="isLoading" class="flex justify-center py-4">
				<i class="nucleo nucleo-loading spin text-2xl text-primary"></i>
			</div>
		</div>
	</div>
</template>

<script>
import requestGQL from "@/http/requests/request";
import CbsDepartments from "@/components/cbs-departments/CbsDepartments.vue";
import moduleDepartment from "@/store/department/moduleDepartment";

export default {
	name: "ApproverDeptRestrictions",
	components: {
		CbsDepartments
	},
	props: {
		stepId: {
			type: [Number, String],
			default: null
		}
	},
	data() {
		return {
			restrictions: [],
			newEmployee: null,
			newDepartments: [],
			isLoading: false,
			isSaving: false
		};
	},
	created() {
		if (!this.$store.state.departmentManagement) {
			this.$store.registerModule("departmentManagement", moduleDepartment);
			this._registeredDeptModule = true;
		}
	},
	beforeDestroy() {
		if (this._registeredDeptModule && this.$store.state.departmentManagement) {
			this.$store.unregisterModule("departmentManagement");
		}
	},
	computed: {
		groupedRestrictions() {
			const groups = {};
			(this.restrictions || []).forEach(r => {
				if (!groups[r.employee_id]) {
					groups[r.employee_id] = {
						employee_id: r.employee_id,
						employee_name: r.employee_name,
						departments: []
					};
				}
				groups[r.employee_id].departments.push({
					department_id: r.department_id,
					department_name: r.department_name
				});
			});
			return Object.values(groups);
		}
	},
	watch: {
		stepId: {
			immediate: true,
			handler(val) {
				if (val) {
					this.fetchRestrictions();
				}
			}
		}
	},
	methods: {
		async fetchRestrictions() {
			if (!this.stepId) return;
			this.isLoading = true;
			try {
				this.restrictions = await requestGQL.request.getApproverDeptRestrictions({
					req_status_id: Number(this.stepId)
				});
			} catch (error) {
				this.$helper.notifyError({ context: this, error });
			} finally {
				this.isLoading = false;
			}
		},
		async saveRestriction() {
			if (!this.newEmployee || !this.newDepartments || this.newDepartments.length === 0) return;
			this.isSaving = true;
			try {
				const employeeId = this.newEmployee.id;
				// Merge with existing departments for this employee
				const existingGroup = this.groupedRestrictions.find(g => g.employee_id === employeeId);
				const existingDeptIds = existingGroup ? existingGroup.departments.map(d => d.department_id) : [];
				const newDeptIds = this.newDepartments.map(d => d.id);
				const mergedDeptIds = [...new Set([...existingDeptIds, ...newDeptIds])];

				await requestGQL.request.syncApproverDeptRestrictions({
					req_status_id: Number(this.stepId),
					employee_id: employeeId,
					department_ids: mergedDeptIds
				});
				this.newEmployee = null;
				this.newDepartments = [];
				await this.fetchRestrictions();
				this.$helper.notifySuccess({
					context: this,
					options: { text: this.$t("DeptRestrictionSaved") }
				});
			} catch (error) {
				this.$helper.notifyError({ context: this, error });
			} finally {
				this.isSaving = false;
			}
		},
		async removeEmployeeRestrictions(employeeId) {
			this.isSaving = true;
			try {
				await requestGQL.request.syncApproverDeptRestrictions({
					req_status_id: Number(this.stepId),
					employee_id: employeeId,
					department_ids: []
				});
				await this.fetchRestrictions();
				this.$helper.notifySuccess({
					context: this,
					options: { text: this.$t("DeptRestrictionRemoved") }
				});
			} catch (error) {
				this.$helper.notifyError({ context: this, error });
			} finally {
				this.isSaving = false;
			}
		},
		async removeDepartment(employeeId, departmentId, group) {
			this.isSaving = true;
			try {
				const remainingDeptIds = group.departments
					.filter(d => d.department_id !== departmentId)
					.map(d => d.department_id);

				await requestGQL.request.syncApproverDeptRestrictions({
					req_status_id: Number(this.stepId),
					employee_id: employeeId,
					department_ids: remainingDeptIds
				});
				await this.fetchRestrictions();
			} catch (error) {
				this.$helper.notifyError({ context: this, error });
			} finally {
				this.isSaving = false;
			}
		}
	}
};
</script>

<style lang="scss" scoped>
.approver-dept-restrictions {
	.restriction-group {
		transition: all 0.2s;
		&:hover {
			border-color: var(--color-gray-3);
		}
	}
	.gap-2 {
		gap: 0.5rem;
	}
	.spin {
		animation: spin 1s linear infinite;
	}
	@keyframes spin {
		from { transform: rotate(0deg); }
		to { transform: rotate(360deg); }
	}
}
</style>
